<template>
  <div class="v-billing-select__wrapper" :class="{ ['opened']: isOpened }">
    <div class="current-item box">
      <div v-if="isOpened" class="white-overflow"></div>
      <div class="item">
        <BillingBankIcon :type="primaryCard.bank_name ? 'bank' : 'card'" />

        <div class="title">
          <div class="default">
            Default
          </div>

          <div class="text">
            <span v-if="primaryCard.bank_name">
              {{ primaryCard.bank_name }} &bull;&bull;&bull;&bull;
              {{ primaryCard.last4 }}
            </span>

            <span v-else>
              &bull;&bull;&bull;&bull;-&bull;&bull;&bull;&bull;-&bull;&bull;&bull;&bull;-{{
                primaryCard.last4
              }}
            </span>
          </div>
        </div>
      </div>

      <div class="chevron" @click="_toggleOpen">
        <i :class="_toggleIcon"></i>
      </div>
    </div>

    <div v-if="isOpened" class="opts-list box">
      <ul>
        <li
          v-for="opt in options"
          :class="{ ['active']: opt.id === primaryCard.id }"
        >
          <div class="item-left item-left__wrapper">
            <div class="radio-wrapper">
              <VRadioInput
                :label="getRadioLabel(opt.id)"
                :checked="opt.id === primaryCard.id"
                :value="opt.id"
                @input="setDefaultPaymentHandler"
              ></VRadioInput>
            </div>
            <div class="item-left">
              <BillingBankIcon :type="opt.bank_name ? 'bank' : 'card'" />
              <div>
                <span v-if="opt.bank_name">
                  {{ opt.bank_name }} &bull;&bull;&bull;&bull; {{ opt.last4 }}
                </span>

                <span v-else>
                  &bull;&bull;&bull;&bull;-&bull;&bull;&bull;&bull;-&bull;&bull;&bull;&bull;-{{
                    opt.last4
                  }}
                </span>
              </div>
            </div>
          </div>

          <div
            v-if="opt.id !== primaryCard.id"
            class="remove-item"
            :class="{
              ['deleting']: isPaymentSourceRemoving && removingId === opt.id
            }"
            @click="_remove(opt.id)"
          >
            <WithLoader
              v-if="isPaymentSourceRemoving && removingId === opt.id"
              :isLoading="true"
              extraClass="billing-select-loader"
            ></WithLoader>
            <img v-else :src="deleteIcon" alt="remove" />
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import { mapActions, mapGetters } from "vuex";

import withToasted from "@/mixins/withToasted";

import VRadioInput from "@/components/VRadioInput";
import WithLoader from "@/components/WithLoader";

import bankIcon from "@/assets/images/billing/Bank.svg";
import cardIcon from "@/assets/images/billing/Card.svg";
import deleteIcon from "@/assets/images/Delete.svg";

const BillingBankIcon = Vue.component("BillingBankIcon", {
  props: ["type"],

  data() {
    return {
      bankIcon,
      cardIcon
    };
  },

  template: `
        <div class="icon">
          <img :src="_icon" alt="select-logo" />
        </div>
  `,

  computed: {
    _icon() {
      return this.type === "card" ? cardIcon : bankIcon;
    }
  }
});

const Radio = Vue.component("radio", {
  props: ["title", "id", "isActive"],
  template: `
      <div class="radio__wrapper" :class="{['active']: isActive}" @click="onClick">
          <div class="radio-circle"></div>
          <div class="radio-title">{{ title }}</div>
      </div>
  `,

  methods: {
    onClick() {
      this.$emit("click", id);
    }
  }
});

export default Vue.component("BillingSelect", {
  props: {
    options: VueTypes.arrayOf(
      VueTypes.shape({
        label: VueTypes.string,
        id: VueTypes.string
      })
    ),

    primaryCard: VueTypes.any
  },

  components: {
    BillingBankIcon,
    Radio,
    VRadioInput,
    WithLoader
  },

  mixins: [withToasted],

  data() {
    return {
      isOpened: false,
      deleteIcon
    };
  },

  methods: {
    _toggleOpen() {
      this.isOpened = !this.isOpened;
    },

    _remove(id) {
      if (!this.isPaymentSourceRemoving) {
        this.removeCreditCard({
          id,
          onSuccess: () => {
            this.toast("The card has been deleted successfully!");
          }
        });
      }
    },

    getRadioLabel(id) {
      return id === this.primaryCard.id ? "Default" : "Set as default";
    },

    setDefaultPaymentHandler({ value: id }) {
      this.setPrimaryCard({
        id,
        onSuccess: () => {
          this.toast("Your primary payment method has been changed!");
        },

        onError: e => {
          const message = e.description
            ? e.description
            : "Something went wrong. Payment method haven't been changed.";
          this.toast(message, "error");
        }
      });
    },

    ...mapActions("billing", ["setPrimaryCard", "removeCreditCard"])
  },

  computed: {
    _toggleIcon() {
      return this.isOpened ? "fal fa-times" : "fa fa-chevron-down";
    },

    ...mapGetters("billing", ["isPaymentSourceRemoving", "removingId"])
  }
});
</script>

<style lang="scss" scoped>
$ns: "v-billing-select";

.#{$ns} {
  &__wrapper {
    .white-overflow {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.8);
    }

    .box {
      border-radius: 5px;
      background: $white;
      box-shadow: 2px 2px 25px rgba(0, 0, 0, 0.1);
    }

    .icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border: 1px solid $title-text-color;
      border-radius: 4px;
      margin-right: 10px;

      img {
        width: 24px;
        height: 24px;
      }
    }

    .item {
      display: flex;
      align-items: center;
    }

    .current-item {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      box-shadow: 2px 2px 25px rgba(0, 0, 0, 0.1);
      padding: 13px 25px 13px 20px;

      .title {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        height: 40px;
        padding: 3px 0 2px 0;

        .default {
          color: $primary-color;
          font-size: 10px;
          font-family: "Lato-Bold", sans-serif;
        }

        .text {
          font-family: "Lato-Regular", sans-serif;
          font-size: 15px;
          color: $main-text-color;
        }
      }

      .chevron {
        position: absolute;
        right: 25px;
        z-index: 1;
        cursor: pointer;

        i {
          font-size: 18px;
          color: $secondary-text-color;
        }
      }
    }

    .opts-list {
      position: relative;
      margin-top: 32px;
      padding: 28px 15px 30px 20px;

      max-height: 400px;
      overflow-y: auto;

      li {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 30px;

        .item-left {
          display: flex;
          align-items: center;

          .radio-wrapper {
            margin-right: 30px;
          }
        }

        &:last-child {
          margin-bottom: 0;
        }

        .remove-item {
          cursor: pointer;
        }

        &.active {
          .radio-wrapper {
            margin-right: 70px;
          }
        }
      }

      .overlap {
        width: 100%;
        height: 400px;
        position: absolute;
        z-index: 1;
        background: rgba(255, 255, 255, 0.8);
        top: 0;
        left: 0;
      }
    }

    &.opened {
      .chevron {
        i {
          font-size: 20px;
          color: $secondary-text-color;
        }
      }
    }

    /deep/ .billing-select-loader {
      width: 30px;
      height: $input-height;

      svg {
        height: 40px;
      }
    }
  }
}
</style>
